import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, Product, ProductDetail, ProductCategory } from "@/lib/types"

interface ProductFilters {
  search?: string
  category_id?: string
  is_active?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: ProductFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useProducts(filters: ProductFilters = {}) {
  return useQuery({
    queryKey: ["products", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<Product>>(`/api/v1/products${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useProduct(id?: string) {
  return useQuery({
    queryKey: ["products", id],
    queryFn: () => api.get<{ data: ProductDetail }>(`/api/v1/products/${id}`),
    enabled: !!id,
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post<{ data: Product }>("/api/v1/products", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: Product }>(`/api/v1/products/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  })
}

export function useProductCategories() {
  return useQuery({
    queryKey: ["product_categories"],
    queryFn: () => api.get<{ data: ProductCategory[] }>("/api/v1/product_categories"),
  })
}

export function useCreateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post("/api/v1/product_categories", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product_categories"] }),
  })
}

export function useUpdateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/product_categories/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product_categories"] }),
  })
}

export function useDeleteProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/product_categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product_categories"] }),
  })
}
